import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  user,
  updateProfile,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { UserInterface, UserRole } from '../../core/models';
import {
  doc,
  DocumentSnapshot,
  Firestore,
  getDoc,
  setDoc,
  Timestamp,
} from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private firebaseAuth: Auth = inject(Auth);
  private router: Router = inject(Router);
  private db: Firestore = inject(Firestore);

  user$ = user(this.firebaseAuth);
  currentUserSig = signal<UserInterface | null | undefined>(undefined);

  constructor() {
    //    this.listenToAuthState();
  }

  register(
    username: string,
    email: string,
    password: string,
    tel: string,
    title: string,
  ): Observable<any> {
    return new Observable((observer) => {
      createUserWithEmailAndPassword(this.firebaseAuth, email, password)
        .then((userCredential) => {
          updateProfile(userCredential.user, { displayName: username })
            .then(() => {
              const userData: UserInterface = {
                id: userCredential.user.uid,
                email: userCredential.user.email!,
                tel: tel,
                title: title,
                username: username,
                role: UserRole.User,
                createdAt: Timestamp.fromDate(new Date()),
              };

              const userDocRef = doc(this.db, 'users', userCredential.user.uid);
              setDoc(userDocRef, userData)
                .then(() => {
                  this.currentUserSig.set(userData);
                  observer.next(userCredential.user);
                  observer.complete();
                })
                .catch((error) => observer.error(error));
            })
            .catch((error) => observer.error(error));
        })
        .catch((error) => observer.error(error));
    });
  }

  // Login method
  login(email: string, password: string): Observable<any> {
    return new Observable((observer) => {
      signInWithEmailAndPassword(this.firebaseAuth, email, password)
        .then((userCredential) => {
          const user = userCredential.user;
          // Fetch the user data from Firestore
          this.getUserData(user.uid).subscribe((userData) => {
            if (userData) {
              this.currentUserSig.set(userData);
              observer.next(userData); // Return the user data
            } else {
              observer.next(null); // No user data found
            }
          });
        })
        .catch((error) => observer.error(error)); // Handle login error
    });
  }

  // Logout the current user
  logout(): Observable<void> {
    return from(
      signOut(this.firebaseAuth)
        .then(() => {
          this.router.navigate(['/login']); // Navigate to login page after logout
        })
        .catch((error) => {
          console.error('Logout error: ', error);
        }),
    );
  }

  // Get the current authenticated user
  getCurrentUser(): any {
    return this.currentUserSig();
  }

  // Method to get the ID token of the currently authenticated user
  getIdToken(): Promise<string | null> {
    const user = this.firebaseAuth.currentUser;
    if (user) {
      return user.getIdToken(); // Returns a promise with the token
    }
    return Promise.resolve(null); // No user is logged in
  }
  // Check if the user is logged in
  isAuthenticated(): boolean {
    const user = this.currentUserSig();
    return user !== null && user !== undefined;
  }

  isAdmin(): boolean {
    const user = this.currentUserSig();
    return user?.role === UserRole.Admin;
  }

  // Get user data by UID from Firestore
  getUserData(uid: string): Observable<UserInterface | null> {
    const userDocRef = doc(this.db, 'users', uid);
    return from(getDoc(userDocRef)).pipe(
      map((docSnapshot: DocumentSnapshot) =>
        docSnapshot.exists() ? (docSnapshot.data() as UserInterface) : null,
      ),
      catchError((error: Error) => {
        console.error('Error fetching user data:', error);
        return of(null);
      }),
    );
  }

  // Initialize user state based on auth status
  private destroy$ = new Subject<void>();

  initUser(): void {
    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.getUserData(user.uid)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (userData) => {
              if (userData) {
                this.currentUserSig.set(userData);
                const currentUrl = this.router.url;

                if (currentUrl === '/' || currentUrl === '/login') {
                  this.router.navigateByUrl('/dashboard');
                }
              } else {
                console.log('No user data found in Firestore.');
                this.currentUserSig.set(null);
              }
            },
            (error) => {
              console.error('Error fetching user data:', error);
              this.currentUserSig.set(null);
            },
          );
      } else {
        console.log('No Firebase user detected.');
        this.currentUserSig.set(null);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
